import type { CSSObject } from 'styled-components';
import { amber, green, red } from '@mui/material/colors';
import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-tree-view/themeAugmentation';
import {
  darken,
  alpha,
  lighten,
  createTheme as createMuiTheme,
  Palette,
} from '@mui/material/styles';
import { RecursivePartial } from '~types/helpers';

const defaultTheme = createMuiTheme();
type ThemeMUI = typeof defaultTheme;

const lightColors = {
  // Brand colors
  primary: '#0CBDF8',
  primaryText: '#015227',
  primaryMuted: '#7BABB6',
  onPrimary: '#ffffff', // color when text/icon/etc is on primary color
  secondary: '#7BABB6',

  // Informative colors
  info: '#3B82F6',
  infoText: '#0A45A6',
  infoMuted: '#cfdef7',
  success: '#29bf4a',
  successText: '#06734E',
  successMuted: '#cee8df',
  warn: '#FBBF24',
  warnText: '#8a6200',
  warnMuted: '#FFCF86',
  error: '#EB5F57',
  errorText: '#8C0606',
  errorMuted: '#FF9D97',

  // Muted gray colors
  // Based on: https://developer.apple.com/design/human-interface-guidelines/ios/visual-design/color/
  muted1: '#8e8e93',
  muted2: '#aeaeb2',
  muted3: '#c7c7cc',
  muted4: '#d1d1d1',
  muted5: '#eaeaea',
  muted6: '#f8f8f8',

  // Interaction plus generic colors for text, surfaces, borders, etc.
  text: '#222222',
  textMuted: '#666666',
  border: 'rgba(150, 150, 150, 0.3)',
  hoverHighlight: 'rgba(150,150,150,0.1)',
  activeHighlight: 'rgba(150,150,150,0.2)',
  focusRing: '#009A48',
  backdrop: 'rgba(0,0,0,0.5)', // modal backdrop etc.
  surface: '#F2F2F2', // cards, sidebars, navbars, etc.
  elevated: '#FFFFFF', // modals, drawers, etc.
  background: '#F8F8F8', // app main background
};

// TODO: fine-tune dark mode colors (eg. update vibrancy)
const darkColors = {
  // Brand colors
  primary: '#0CBDF8',
  primaryText: '#1cff87',
  primaryMuted: '#24392a',
  onPrimary: '#ffffff', // color when text/icon/etc is on primary color
  secondary: '#7BABB6',

  // Informative colors
  info: '#3B82F6',
  infoText: '#81aef7',
  infoMuted: '#1b2940',
  success: '#10B981',
  successText: '#1ee8a5',
  successMuted: '#193328',
  warn: '#FBBF24',
  warnText: '#ffc93d',
  warnMuted: '#40351a',
  error: '#EF4444',
  errorText: '#ff7070',
  errorMuted: '#3e1c1d',

  // Muted gray colors
  muted1: '#8e8e93',
  muted2: '#636366',
  muted3: '#48484a',
  muted4: '#3a3a3c',
  muted5: '#2c2c2e',
  muted6: '#1d1d1f',

  // Interaction plus generic colors for text, surfaces, borders, etc.
  text: '#FFFFFF',
  textMuted: '#999999',
  border: 'rgba(150, 150, 150, 0.3)',
  hoverHighlight: 'rgba(150,150,150,0.08)',
  activeHighlight: 'rgba(150,150,150,0.2)',
  focusRing: '#009A48',
  backdrop: 'rgba(0,0,0,0.5)', // modal backdrop etc.
  surface: '#222222', // cards, sidebars, navbars, etc.
  elevated: '#333333', // modals, drawers, etc.
  background: '#111111', // app main background
};

const rem = (px: number) => `${px / 16}rem`;
const em = (px: number) => `${px / 16}em`;

export const breakpoints = {
  phone: { min: 0, max: 767 },
  tablet: { min: 768, max: 1024 },
  desktop: { min: 1025, max: 1800 },
  monitor: { min: 1801, max: Infinity },
};

const media = {
  phone: `@media (max-width: ${em(breakpoints.phone.max)})`,
  tablet: `@media (min-width: ${em(breakpoints.tablet.min)}) and (max-width: ${em(breakpoints.tablet.max)})`, // prettier-ignore
  tabletDown: `@media (max-width: ${em(breakpoints.tablet.max)})`,
  tabletUp: `@media (min-width: ${em(breakpoints.tablet.min)})`,
  desktop: `@media (min-width: ${em(breakpoints.desktop.min)}) and (max-width: ${em(breakpoints.desktop.max)})`, // prettier-ignore
  desktopDown: `@media (max-width: ${em(breakpoints.desktop.max)})`,
  desktopUp: `@media (min-width: ${em(breakpoints.desktop.min)})`,
  monitor: `@media (min-width: ${em(breakpoints.monitor.min)})`,
};

export const theme = {
  rem,
  media,
  colors: lightColors,
  typography: {
    title1: { fontSize: rem(42), fontWeight: 600 } as CSSObject,
    title2: { fontSize: rem(32), fontWeight: 600 } as CSSObject,
    title3: { fontSize: rem(24), fontWeight: 700 } as CSSObject,
    title4: { fontSize: rem(20), fontWeight: 700 } as CSSObject,
    bodyLarge: { fontSize: rem(18), fontWeight: 500 } as CSSObject,
    bodyLargeStrong: { fontSize: rem(18), fontWeight: 600 } as CSSObject,
    body: { fontSize: rem(16), fontWeight: 400 } as CSSObject,
    bodyStrong: { fontSize: rem(16), fontWeight: 600 } as CSSObject,
    bodySmall: { fontSize: rem(12), fontWeight: 400 } as CSSObject,
    bodySmallStrong: { fontSize: rem(12), fontWeight: 600 } as CSSObject,
    caption: { fontSize: rem(10), fontWeight: 400 } as CSSObject,
    overline: {
      fontSize: rem(10),
      textTransform: 'uppercase',
      fontWeight: 600,
      letterSpacing: 1.2,
    } as CSSObject,
  },
  spacing: {
    none: 0,
    xxsmall: 4,
    xsmall: 8,
    small: 12,
    normal: 16,
    medium: 24,
    large: 32,
    xlarge: 48,
    xxlarge: 56,
    xxxlarge: 72,
  },
  sizing: {
    button: {
      xsmall: 18,
      small: 24,
      normal: 32,
      medium: 44,
      large: 60,
    },
    icon: {
      xsmall: 14,
      small: 18,
      normal: 24,
      large: 32,
    },
  },
  radii: {
    small: 4,
    normal: 8,
    medium: 16,
    large: 24,
    full: 999,
    inputField: 32,
  },
  shadows: {
    small: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    normal: '0 0px 10px 0px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // prettier-ignore
    medium: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // prettier-ignore
    large: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // prettier-ignore
  },
};

export const darkTheme = {
  ...theme,
  colors: darkColors,
};

const getPalette = (options: Theme) => {
  return {
    primary: {
      main: options.colors.primary,
    },
    secondary: {
      main: options.colors.primary, // TODO: Add secondary color
    },
    commenting: {
      main: 'rgba(240, 240, 0, 1)',
      selected: options.colors.primary, // TODO: Add secondary color
    },
  };
};

const getMuiTheme = (customTheme: any, options: Theme) => {
  const optionsPalette = getPalette(options);

  // Default grey colors
  const grey = {
    ...defaultTheme.palette.grey,
    // Add some greys
    80: '#f7f7f7', // TODO: or just just 100 (f5f5f5)
    250: '#ebebeb',
    300: '#D2D2D2',
    450: '#C3C3C3',
    500: 'rgba(69, 69, 69, 0.87)',
  };

  // Default palette
  const palette = {
    common: defaultTheme.palette.common,
    primary: {
      main: optionsPalette.primary.main,
      light: undefined,
      dark: undefined,
    },
    secondary: {
      main: optionsPalette.secondary.main,
      light: undefined,
      dark: undefined,
      contrastText: defaultTheme.palette.common.white,
    },
    // CUSTOM: success
    success: {
      main: optionsPalette.primary.main,
    },
    // CUSTOM: warning
    warning: {
      main: amber[400],
    },
    // CUSTOM: use darker red for more contrast (a11y)
    error: {
      ...defaultTheme.palette.error,
      main: defaultTheme.palette.error.dark,
    },
    grey,
    text: { primary: theme.colors.text },
    divider: defaultTheme.palette.divider,
    background: {
      default: grey[80], // TODO: or just use 100?
      paper: defaultTheme.palette.common.white,
      card: defaultTheme.palette.common.white, // CUSTOM
      dock: defaultTheme.palette.common.white, // CUSTOM
      dark: grey[300], // CUSTOM
      thumbnail: defaultTheme.palette.common.white, // grey[200], // CUSTOM
      preview: grey[300], // grey[400], // CUSTOM
    },
    // CUSTOM: border
    border: {
      section: grey[250],
      paper: grey[250],
      card: '#C5C5C5',
    },
    action: defaultTheme.palette.action,
    commenting: {
      main: optionsPalette.commenting.main,
      selected: optionsPalette.commenting.selected,
      status: {
        success: green[400],
        waiting: amber[400],
        warning: red[400],
        empty: '#D6E3E8',
      },
    },
  } as RecursivePartial<Palette>;

  // Override MUI theme defaults
  const muiTheme: RecursivePartial<ThemeMUI> = {
    // Palette
    // https://material-ui.com/customization/palette/
    palette,

    // Typography
    // https://material-ui.com/customization/typography/
    typography: {
      // h1: title in the main view (folder name, product name, etc)
      h1: {
        fontSize: '1rem',
        lineHeight: 1.66,
        letterSpacing: '0.00938em',
        fontWeight: 500,
      },

      // h2: subtitle in the main view (selected workspace/file name, pages, etc.)
      h2: {
        fontSize: '0.9rem',
        lineHeight: 1.66,
        letterSpacing: '0.00938em',
        fontWeight: 500,
      },

      // h3: modal main title
      h3: {
        fontSize: '1rem',
        lineHeight: 1.66,
        letterSpacing: '0.00938em',
        fontWeight: 400,
      },

      // h4 - h6: currently not used
      h4: {},
      h5: {},
      h6: {},

      // caption: file and folder name beneath the thumbnail
      caption: {
        fontSize: '0.8rem',
        fontWeight: 500,
      },

      // subtitle1: field names in forms, etc.
      subtitle1: {
        fontSize: '0.8rem',
        fontWeight: 500,
      },

      // subtitle2: text within fields in forms, etc.
      subtitle2: {
        fontSize: '0.8rem',
        fontWeight: 400,
        letterSpacing: '0.00938em',
      },

      // body1: misc text (folder tree, autocompletion, etc)
      body1: {
        fontSize: '0.9rem',
        fontWeight: 400,
        letterSpacing: '0.00938em',
      },

      // body2: misc text (folder tree, autocompletion, etc)
      body2: {
        fontSize: '0.8rem',
        fontWeight: 400,
      },

      button: {},

      overline: {},
    },

    // Component default prop values
    // https://material-ui.com/customization/globals/#default-props
    components: {
      MuiInputBase: {
        defaultProps: {
          // Needed to prevent adding a global style for every input field
          disableInjectingGlobalStyles: true,
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          clearIndicator: {
            zIndex: 998,
          },
          tag: {
            zIndex: 998,
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          position: 'static' as const,
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: 'none' as const,
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: theme.radii.medium,
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          slotProps: {
            popper: {
              onClick: (e: any) => e.stopPropagation(),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            zIndex: 9999,
          },
        },
      },
      // TODO: Avoid using important
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: `${options.colors.error} !important`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            color: options.colors.text,
            borderRadius: options.radii.inputField,
            fieldset: {
              borderRadius: options.radii.inputField,
              background: options.colors.muted5,
            },
            label: {
              color: `${options.colors.text} !important`,
            },
            input: {
              color: `${options.colors.text} !important`,
              transition: 'background-color 2147483647s',
              zIndex: 999,
            },
            textarea: {
              color: `${options.colors.text} !important`,
              zIndex: 999,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderRadius: options.radii.full,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            borderRadius: options.radii.full,
            background: options.colors.muted5,
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            minWidth: 170,
            borderRadius: options.radii.full,
            // TODO: Avoid using important
            '& *': {
              color: `${options.colors.text} !important`,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {},
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: options.colors.text,
            '&.Mui-selected': {
              fontWeight: 'bold',
              color: options.colors.text,
            },
          },
        },
      },
      MuiTreeItem: {
        styleOverrides: {
          root: {
            padding: 0,
          },
          content: {
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          label: {
            padding: 0,
            height: 50,
            display: 'flex',
            '& > div': {
              padding: 0,
            },
            '&:hover': {
              padding: 0,
              backgroundColor: 'transparent',
            },
          },
          iconContainer: {
            padding: 0,
            width: 0,
            marginRight: '0', // TODO: 8px ?
          },
          groupTransition: {
            padding: 0,
            marginLeft: '0', // TODO: 8px ?
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            border: 'none',
            padding: `${theme.spacing.xsmall}px ${theme.spacing.small}px`,
          },
        },
      },
    },
    // Component CSS overrides
    // https://material-ui.com/customization/globals/#css
    // overrides: {
    //   MuiToolbar: {
    //     regular: {
    //       '@media (min-width: 600px)': {
    //         minHeight: '56px',
    //       },
    //     },
    //     gutters: {
    //       '@media (min-width: 600px)': {
    //         paddingLeft: '16px',
    //         paddingRight: '16px',
    //       },
    //     },
    //   },
    //   MuiButtonBase: {
    //     root: {
    //       whiteSpace: 'nowrap' as const,
    //     },
    //   },
    //   MuiButton: {
    //     outlined: {
    //       backgroundColor: palette.common.white,
    //     },
    //     textPrimary: {
    //       color: customTheme.palette.primary.dark,
    //       '&:hover': {
    //         backgroundColor: customTheme.palette.primary.lightFade,
    //       },
    //     },
    //     textSecondary: {
    //       color: customTheme.palette.secondary.dark,
    //     },
    //     outlinedPrimary: {
    //       borderColor: customTheme.palette.primary.darker,
    //       color: customTheme.palette.primary.dark,
    //       '&:hover': {
    //         backgroundColor: customTheme.palette.primary.lightFade,
    //       },
    //     },
    //     outlinedSecondary: {
    //       borderColor: customTheme.palette.primary.darker,
    //       color: customTheme.palette.secondary.dark,
    //     },
    //     outlinedSizeSmall: {
    //       padding: '2px 9px',
    //     },
    //   },
    //   MuiListItemIcon: {
    //     root: {
    //       minWidth: '32px',
    //     },
    //   },
    //   MuiListItemIcon: {
    //     root: {
    //       minWidth: '32px',
    //     },
    //   },
    //   MuiInputLabel: {
    //     root: {
    //       color: palette.common.black,
    //       fontSize: '0.8rem',
    //       fontWeight: 500,
    //       lineHeight: 1.75,
    //       letterSpacing: '0.00938em',
    //     },
    //   },
    //   MuiFormLabel: {
    //     root: {
    //       color: palette.common.black,
    //     },
    //   },
    //   MuiPaper: {
    //     root: {
    //       border: `1px solid ${palette.border.paper}`,
    //     },
    //   },
    //   MuiTabs: {
    //     indicator: {
    //       height: '3px',
    //     },
    //   },
    //   MuiTab: {
    //     root: {
    //       textTransform: 'none' as const,
    //       '&$selected': {
    //         color: palette.primary.main,
    //       },
    //     },
    //   },
    //   /*
    //   // TODO: remove? (was added for search quick fix)
    //   MuiFormLabel: {
    //     root: {
    //       fontSize: '0.8rem',
    //       fontWeight: 500,
    //       paddingBottom: '4px',
    //     },
    //   },
    //   */
    //   MuiRadio: {
    //     root: {
    //       padding: '2px 9px',
    //     },
    //   },
    //   MuiTreeItem: {
    //     root: {
    //       '&.Mui-selected-item': {
    //         '& > .MuiTreeItem-content': {
    //           backgroundColor: customTheme.palette.primary.lightFade,
    //         },
    //         '& > .MuiTreeItem-content > .MuiTreeItem-iconContainer': {
    //           color: palette.primary.main,
    //           fontWeight: 500,
    //         },
    //         '& > .MuiTreeItem-content > .MuiTreeItem-label': {
    //           color: palette.primary.main,
    //           fontWeight: 500,
    //         },
    //       },
    //       '&.Mui-selected-item-parent': {
    //         '& > .MuiTreeItem-content > .MuiTreeItem-iconContainer': {
    //           color: palette.primary.main,
    //           fontWeight: 500,
    //         },
    //         '& > .MuiTreeItem-content > .MuiTreeItem-label': {
    //           color: palette.primary.main,
    //           fontWeight: 500,
    //         },
    //       },
    //       '&:focus > $content': {
    //         backgroundColor: `${customTheme.palette.primary.darkFade} !important`,
    //       },
    //       '&:focus > $content > $label': {
    //         backgroundColor: 'transparent',
    //       },
    //       '&.drag-hover': {
    //         backgroundColor: customTheme.palette.primary.darkFade,
    //       },
    //     },
    //     content: {
    //       '&:hover': {
    //         backgroundColor: customTheme.palette.primary.lightFade,
    //       },
    //     },
    //     label: {
    //       '& > div': {
    //         padding: '4px 0',
    //       },
    //       '&:hover': {
    //         backgroundColor: 'transparent',
    //       },
    //     },
    //     iconContainer: {
    //       marginRight: '8px', // TODO: 8px ?
    //     },
    //     group: {
    //       marginLeft: '12px', // TODO: 8px ?
    //     },
    //   },
    //   MuiTooltip: {
    //     tooltip: {
    //       fontSize: '12px',
    //     },
    //   },
    // },
  };

  return muiTheme;
};

const getCustomTheme = (options: Theme) => {
  const palette = getPalette(options);
  // Custom theme variables
  // https://material-ui.com/customization/theming/#custom-variables
  return {
    custom: {
      transitions: {
        duration: '150ms',
      },
    },
    palette: {
      primary: {
        lightest: lighten(palette.primary.main, 0.5),
        lighter: lighten(palette.primary.main, 0.1),
        light: lighten(palette.primary.main, 0.2),
        dark: darken(palette.primary.main, 0.3),
        darker: darken(palette.primary.main, 0.05),
        lightFade: alpha(palette.primary.main, 0.09),
        darkFade: alpha(palette.primary.main, 0.16),
      },
      secondary: {
        lighter: lighten(palette.secondary.main, 0.1),
        light: lighten(palette.secondary.main, 0.2),
        dark: darken(palette.secondary.main, 0.3),
        darker: darken(palette.secondary.main, 0.05),
        lightFade: alpha(palette.secondary.main, 0.09),
        darkFade: alpha(palette.secondary.main, 0.16),
      },
      custom: {
        fadedTransparent: 'rgba(0, 0, 0, 0.06)',
        darkTransparent: 'rgba(0, 0, 0, 0.12)',
        darkerTransparent: 'rgba(0, 0, 0, 0.26)',
        neutralAccent: '#81dcff',
      },
    },
  };
};

const getShadows = (muiTheme: ThemeMUI, reduction: number) => {
  const shadows = muiTheme.shadows;
  return {
    // Remove slight shadows, and make all other shadows a little more flat.
    shadows: shadows.map((value, index) => {
      return index <= reduction - 1 ? 'none' : shadows[index - reduction];
    }),
  };
};
export const createTheme = (options: Theme) => {
  const shadows = getShadows(defaultTheme, 2);
  const customTheme = getCustomTheme(options);
  const muiTheme = getMuiTheme(customTheme, options);
  const theme = createMuiTheme({ ...muiTheme, ...shadows } as any as any);
  // TODO: use responsive font sizes or not?

  // console.log('>> END THEME', theme);
  return theme; // responsiveFontSizes(theme);
};

export type Theme = typeof theme;
export type Typography = keyof Theme['typography'];
export type Color = keyof Theme['colors'];
export type Spacing = keyof Theme['spacing'];
export type Radius = keyof Theme['radii'];
export type Breakpoint = keyof Theme['media'];
export type Shadow = keyof Theme['shadows'];
export type Size = keyof Theme['sizing'];
